/**
 * Wishlist heart-toggle (shop cards — content-product.php; the
 * single-product page reuses the same .craftis-wishlist-toggle button
 * markup if/when it adds one). localStorage is the source of truth in the
 * browser — works instantly, no round-trip, and covers guests with zero
 * backend state. For a logged-in user, every change is also pushed to
 * usermeta via craftis_save_wishlist (inc/wishlist.php), and
 * craftisWishlistL10n.initialItems (that same usermeta, read on page load)
 * is merged in once so the wishlist still shows correct after logging in
 * on a different browser/device.
 */

const STORAGE_KEY = 'craftisWishlist';

function readWishlist() {
	try {
		const raw = window.localStorage.getItem( STORAGE_KEY );
		const parsed = raw ? JSON.parse( raw ) : [];
		return Array.isArray( parsed ) ? parsed.map( String ) : [];
	} catch ( e ) {
		return [];
	}
}

function writeWishlist( ids ) {
	try {
		window.localStorage.setItem( STORAGE_KEY, JSON.stringify( ids ) );
	} catch ( e ) {
		// localStorage unavailable (private browsing, quota) — the toggle
		// still works for the current page view via the DOM state alone,
		// it just won't persist across a reload.
	}
}

function syncToServer( ids ) {
	if ( ! window.craftisWishlistL10n || ! window.craftisWishlistL10n.isLoggedIn ) {
		return;
	}

	const body = new URLSearchParams();
	body.append( 'action', 'craftis_save_wishlist' );
	body.append( 'nonce', window.craftisWishlistL10n.nonce );
	ids.forEach( ( id ) => body.append( 'product_ids[]', id ) );

	fetch( window.craftisWishlistL10n.ajaxUrl, {
		method: 'POST',
		credentials: 'same-origin',
		headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
		body: body.toString(),
	} ).catch( () => {
		// Best-effort — localStorage already holds the current state
		// regardless of whether the server mirror succeeds.
	} );
}

function setButtonState( button, isActive ) {
	const icon = button.querySelector( '.craftis-icon--heart, .craftis-icon--heart-filled' );

	button.classList.toggle( 'is-active', isActive );
	button.setAttribute( 'aria-pressed', isActive ? 'true' : 'false' );
	button.setAttribute(
		'aria-label',
		isActive ? window.craftisWishlistL10n.removeLabel : window.craftisWishlistL10n.addLabel
	);

	if ( icon ) {
		icon.classList.toggle( 'craftis-icon--heart', ! isActive );
		icon.classList.toggle( 'craftis-icon--heart-filled', isActive );
	}
}

function refreshAllButtons( ids ) {
	document.querySelectorAll( '[data-craftis-wishlist-toggle]' ).forEach( ( button ) => {
		setButtonState( button, ids.includes( button.dataset.productId ) );
	} );
}

function initWishlist() {
	let ids = readWishlist();

	// Merge in the logged-in user's server-saved wishlist once on load —
	// union rather than overwrite, so an item saved as a guest just before
	// logging in isn't dropped.
	if ( window.craftisWishlistL10n && Array.isArray( window.craftisWishlistL10n.initialItems ) ) {
		const serverIds = window.craftisWishlistL10n.initialItems.map( String );
		ids = Array.from( new Set( [ ...ids, ...serverIds ] ) );
		writeWishlist( ids );
	}

	refreshAllButtons( ids );

	document.addEventListener( 'click', ( event ) => {
		const button = event.target.closest( '[data-craftis-wishlist-toggle]' );
		if ( ! button ) {
			return;
		}

		event.preventDefault();

		const productId = button.dataset.productId;
		if ( ! productId ) {
			return;
		}

		const current = readWishlist();
		const isActive = current.includes( productId );
		const next = isActive
			? current.filter( ( id ) => id !== productId )
			: [ ...current, productId ];

		writeWishlist( next );
		refreshAllButtons( next );
		syncToServer( next );
	} );
}

document.addEventListener( 'DOMContentLoaded', initWishlist );
